/**
 * # Behavior
 * ## Socket connection & it's auth with couchdb-gateway
 * 
 * ## Handles `BE_MASTER` message from the couchdb-gateaway
 * 
 * ## Handles DOC_CHANGES message from the couchdb-gateway
 * - Listens `DOC_CHANGES` message sent by the couchdb-gateway to socket connection.
 * - On Message, delegates this to the corresponding LocalDatabase to save them; And on
 * successful save to the local database, acknowleges the server.
 * 
 * ## Dispatches `destroyed` event on self when disposed is completed.
 * 
 * ## Write Docs when Master
 * When this instance is the master then listens on the local-storage change for the keys 
 * `writeDocs.$couchdbName.*`, for each new request it forwards the request to the 
 * corresponding RemoteDatabase, and when RemoteDatabase completes it's work it updates
 * the local-storage key for the response.
 * 
 * ## Handles `master-tab-elected` and `dispose` brodcast messages
 * - Read `docs => dev-reference => Messages broadcast between tabs` section for more detail
 * 
 * # Developer Notes:
 * - `dispatch` is no-op function when redux is not integrated.
 */

import { default as _log } from '@dw/loglevel';
import EventEmitter from "@dreamworld/event-emitter";
import sysend from "@dreamworld/sysend";
import { uuidBase62, uuid } from "@dreamworld/uuid-base62";
import { io } from "@dw/socket-io-client-esm";
import Database from "./database.js";
import { AuthStatus } from "./auth-status.js";
import replaySocketRequests from "./replay-socket-requests.js";
import * as localDatabases from "./local-databases.js";
import {
  connectionStatusChanged,
  authChanged,
  destroy,
  masterChanged,
  writeDocsMetadata,
  databaseDeleted
} from "./redux/actions.js";
import {
  connected,
  connectionLost,
  pendingWritesByDbName,
  failedWritesByDbName
} from "./redux/selectors.js";
import * as localDbSyncManagers from "./local-db-sync-managers.js";

//lodash
import forEach from "lodash-es/forEach";
import set from "lodash-es/set";
import sortBy from "lodash-es/sortBy";
import compact from "lodash-es/compact";
import lastIndexOf from 'lodash-es/lastIndexOf';
import * as utils from "./utils.js";

export default class CouchDB extends EventEmitter {

  constructor(name, url, store, deviceId) {
    super();
    this.name = name;
    this.url = url;
    this.store = store;
    this._authStatus = AuthStatus.PENDING;

    this._deviceId = deviceId;
    this._sessionId = uuid();

    // Set logger
    this.log = _log.getLogger(`dreamdb.couchdb.${this.name}`);

    this._connectSocket(url);
    replaySocketRequests(this.name, this.socket);
    this._registerListeners();

    this.master = false;

    /**
     * Hash of the CouchdbDatabases. key = database name, value = instance of CouchdbDatabase.
     * When database is first time used, it's instance is created and this is populated later 
     * on it's never removed from this map.
     */
    this.databases = {};

    /**
     * This method is no-op if dreamdb is not connected with redux. Otherwise it returns redux.dispatch
     */
    this._dispatch = this._dispatch.bind(this);

    // logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.name}`;

    this._dispatch(authChanged({
      couchdbName: this.name,
      status: 'PENDING'
    }));

    this._pendingWriteDocsRequests = {};

    //scheduler to remove responded writeDocs from localStorage
    this._schedulerToRemoveWriteRequests = undefined;

    this._managePendingAndFailedWrites = this._managePendingAndFailedWrites.bind(this);
    this.handleNewWritesRequest = this.handleNewWritesRequest.bind(this);

    this._listenLocalStorageChanges();
    utils._resolveInitPromise(this.name);

    this.disposeInProgress = false;
  }

  /**
   * Returns current auth status.
   * Possible values: `PENDING`, `IN_PROGRESS`, `DONE`, `INVALID_CREDENTIAL`.
   */
  get authStatus() {
    return this._authStatus;
  }

  /**
   * It connects with server via socket connection.
   * @param {String} url socket url
   * @returns {Socket} instance of socket
   */
  _connectSocket(url) {
    let { origin, pathname } = this._parseSocketUrl(url);
    this.log.debug(`_connectSocket: called.`);

    this.socket = io(origin, {
      path: pathname,
      transports: ['websocket'],
      query: {
        clientId: this._sessionId,
        deviceId: this._deviceId,
      }
    });
  }

  /**
   * It sends `SYNCED_DBS` messages to the server.
   */
  async _sendSyncedDbs() {
    this.log.debug(`${this.logPrefix}:_sendSyncedDbs: fetching synced databases...`);
    let syncedDbs = await localDatabases.fetchSyncedDatabases(this);
    this.log.debug(`${this.logPrefix}:_sendSyncedDbs: fetched synced databases. syncedDbs=`, syncedDbs);
    this.socket._emit('SYNCED_DBS', { dbs: syncedDbs }, () => {});
    this.log.debug(`${this.logPrefix}:_sendSyncedDbs: SYNCED_DBS message sent to server`);
  }

  /**
   * It parse given url
   * @param {String} url socket url
   * @returns {Object} It contains origin and pathname
   */
  _parseSocketUrl(url) {
    let oUrl = new URL(url);
    let pathname = oUrl.pathname;
    if (pathname.endsWith("/")) {
      pathname = pathname.substr(0, pathname.length - 1);
    }

    return {
      origin: oUrl.origin,
      pathname: `${pathname}/socket.io`
    };
  }

  _registerListeners() {
    this.socket.on('connect', this._onSocketConnect.bind(this));
    this.socket.on('disconnect', this._onSocketDisconnect.bind(this));
    this.socket.on('connect_error', this._onConnectError.bind(this));

    this.socket.io.on('error', this._onSocketError.bind(this));
    this.socket.io.on('reconnect', this._onSocketReconnect.bind(this));
    this.socket.io.on('reconnect_attempt', this._onSocketReconnectAttempt.bind(this));
    this.socket.io.on('reconnect_error', this._onSocketReconnectError.bind(this));
    this.socket.io.on('reconnect_failed', this._onSocketReconnectFailed.bind(this));

    this.socket.on('BE_MASTER', this._onSocketMasterElected.bind(this));
    this.socket.on('DOC_CHANGES', this._onSocketDocChanges.bind(this));
    this.socket.on('PING', this._onPing.bind(this));
    this.socket.on('DATABASE_DELETED', this._onDatabaseDeleted.bind(this));

    sysend.on('dispose', this._onDispose.bind(this));
    sysend.on(`master-tab-elected`, this._onMasterChanged.bind(this));
  }

  async _onConnectError(e) {
    this.log.info(`${this.logPrefix}._onConnectError(): error=`, e);

    if (e.message === "DEVICE_DISPOSED") {
      await this.dispose();
      window.location.reload();
    }
  }

  /**
   * It is used for authentication
   * @param {String} token 
   * @returns {Promise} which is resolve on successful authentication. rejects if token is invalid.
   */
  auth(token) {
    return new Promise(async (resolve, reject) => {
      this._updateAuthStatus(AuthStatus.IN_PROGRESS);

      this.socket.emit('AUTH_TOKEN', { token }, (response) => {

        if (response.success) {
          this._updateAuthStatus(AuthStatus.DONE);

          this.log.debug(`${this.logPrefix}.auth(): authenticated`);
          utils._resolveAuthPromise(this.name);
          resolve();
          return;
        }

        this._updateAuthStatus(AuthStatus.INVALID_CREDENTIAL);

        this.log.error(`${this.logPrefix}.auth(): invalid credientials`);
        reject(response.error);
      });
    });
  }

  /**
   * It updates authStatus with given status and dispatch event
   * @param {String} authStatus new auth status
   */
  _updateAuthStatus(authStatus) {
    this._authStatus = authStatus;
    this.emit(`auth-status-changed`, authStatus);
    this._dispatch(authChanged({
      couchdbName: this.name,
      status: authStatus
    }));
  }

  /**
   * Returns current sessionId.
   */
  sessionId() {
    return this._sessionId;
  }

  /**
   * It creates and returns Database instance of given dbName.
   * 
   * @param {String} dbName - name of the database
   * @returns {Object} returns Database instance.
   */
  db(dbName) {
    if (!dbName) {
      this.log.error(`${this.logPrefix}.db(): database name is not provided`);
      return;
    }

    if (this.databases[dbName]) {
      return this.databases[dbName];
    }

    let database = new Database(dbName, this, this._dispatch);
    this.databases[dbName] = database;

    return database;
  }

  /**
   * Dispatches redux action if redux is integrated, otherwise it does nothing.
   * 
   * @param {Object} action -  A redux action to be dispatched
   */
  _dispatch(action) {
    if (!this.store) {
      return;
    }

    this.store.dispatch(action);
  }

  /**
   * - All live queries are stopped.
   * - All LocalDatabases will stop listening changes feed.
   * - Disconnects WebSocket connection to the couchdb-gateway.
   * - And all the memories used for this will also freed up.
   * - But, the persisted data (on Pouchdb) won't be destroyed 
   *   they are kept for the future connection.
   * - If this instance was the `master` then couchdb-gateway will elect
   * any other active instance from this device as the `master`.
   */
  disconnect() {

    //It will cancel all live queries
    forEach(this.databases, (databaseRef) => {
      databaseRef.disconnect();
    });

    this.databases = {};

    //close socket connection
    this.socket.close();
  }

  /**
   * Deletes all the local databases created for this couchdb instance.
   * Instructs the couchdb-gateway also to cleanup all the data for this
   * device from it's end.
   * This message will be broadcasted to other active tabs from this device
   * as well. So, they will also perform the disconnection.
   */
  async dispose() {

    this.disposeInProgress = true;

    //Dispatch redux action `destroy`
    this._dispatch(destroy({
      couchdbName: this.name
    }));

    //distroy all local databases
    await localDatabases.destroyAll(this);

    this._removePendingAndFailedWrites();
    this._resetDeviceId();

    //Broadcast `dispose` message, so other tabs can starts work on that.
    sysend.broadcast(`dispose`, { couchdbName: this.name });

    //request to ser ver for destroy data for the device.
    this._destroyRemoteDatabase();

    this.emit('destroyed', { name: this.name });
    this.log.info(`dispose: disposed. name=${this.name}`);
  }

  /**
   * It's handler of localStorage changes.
   * @param {Object} e 
   */
  handleNewWritesRequest(e) {
    this._managePendingAndFailedWrites(e);

    if (!e.key || !e.newValue || !e.key.startsWith(`dreamdb.${this.name}.writeDocs`)) {
      return;
    }

    let requestId = e.key.split(".")[3];
    let value = JSON.parse(e.newValue);

    if (value && value.docs && value.docs.length && !value.response) {
      let pendingWriteDocsRequests = { ...this._pendingWriteDocsRequests };
      this._pendingWriteDocsRequests[requestId] = { requestId, value };

      if (!Object.keys(pendingWriteDocsRequests).length) {
        this._sendDocsToRemoteInRequestedOrder();
      }
    }
  }

  /**
   * It removes pending and failed writes from the local storage for the disposed couchDb
   */
  _removePendingAndFailedWrites() {
    //Remove failedWrites and pendingWrites from localStorage
    let value = JSON.parse(window.localStorage.getItem('dreamdb.pendingFailedWrites'));

    if (value) {
      delete value[`couchdb-${this.name}`];
    }

    window.localStorage.setItem(`dreamdb.pendingFailedWrites`, JSON.stringify(value));
  }

  /**
   * It updates deviceId.
   * It updates second part of deviceId seperated by dash(-).
   */
  _resetDeviceId() {
    let value = window.localStorage.getItem('dreamdb.deviceId');

    if (!value) {
      return;
    }

    let currentId = value.slice(0, lastIndexOf(value, "-"));
    let newDeviceId = `${currentId}-${uuidBase62()}`;
    window.localStorage.setItem(`dreamdb.deviceId`, newDeviceId);
  }

  /**
   * It requests to server to destroy database for current device.
   * It returns promise which is resolved when remote databases destroyed.
   */
  _destroyRemoteDatabase() {
    return new Promise((resolve, reject) => {
      this.socket.emit('DISPOSE', {}, () => {

        //disconnect
        this.socket.close();
        utils._resetPromises(this.name);
        resolve();
      });
    });
  }

  /**
   * Stops listening for the live changes for the given queryIdPrefix.
   * It calls database's `cancelAll` method
   * 
   * @param {String} queryIdPrefix - prefix of the queryId
   */
  cancelAll(queryIdPrefix) {
    if (!queryIdPrefix) {
      this.log.error(`${this.logPrefix}.cancelAll(): queryIdPrefix is required.`);
      return;
    }

    if (typeof queryIdPrefix !== 'string') {
      this.log.error(`${this.logPrefix}.cancelAll(): queryIdPrefix should be type of String.`);
      return;
    }

    forEach(this.databases, (databaseRef) => {
      databaseRef.cancelAll(queryIdPrefix);
    });

    this.log.info(`${this.logPrefix}.cancelAll(): All queries from couchdb=${this.name} has been cancelled which is startsWith=${queryIdPrefix}.`);
  }

  /**
   * It's socket's `connect` event handler
   * Fired upon new socket connection.
   * @param {Socket} socket 
   */
  _onSocketConnect(socket) {
    this.log.debug(`_onSocketConnect: called. socket=`, socket);
    this._sendSyncedDbs();
    this._dispatch(connectionStatusChanged({
      couchdbName: this.name,
      connected: true,
      connectionLost: false
    }));

    this.log.debug(`${this.logPrefix}._onSocketConnect(): connection established:`, socket);
  }

  /**
   * It's socket's `error` event handler
   * Fired upon a connection error.
   * @param {Object} error
   */
  _onSocketError(error) {
    const state = this.store.getState();
    const currentConnected = connected(state, this.name);
    const currentConnectionLost = connectionLost(state, this.name);

    this._dispatch(connectionStatusChanged({
      couchdbName: this.name,
      connected: currentConnected,
      connectionLost: currentConnectionLost,
      error: error.message
    }));

    this.log.warn(`${this.logPrefix}._onSocketError(): socket error:`, error);
  }

  /**
   * Fired upon a successful reconnection.
   * @param {Number} attempt reconnection attempt number
   */
  async _onSocketReconnect(attempt) {
    this.log.debug(`${this.logPrefix}._onSocketReconnect(): socket reconnected after attempt: ${attempt}`);
  }

  /**
   * Fired upon an attempt to reconnect.
   * @param {Number} attempt reconnection attempt number
   */
  _onSocketReconnectAttempt(attempt) {
    this.log.debug(`${this.logPrefix}._onSocketReconnectAttempt(): socket reconnect attempt: ${attempt}`);
  }

  /**
   * Fired upon a reconnection attempt error.
   * @param {Object} error 
   */
  _onSocketReconnectError(error) {
    this.log.debug(`${this.logPrefix}._onSocketReconnectError(): socket reconnect error: `, error);
  }

  /**
   * Fired when couldn't reconnect within `reconnectionAttempts`.
   */
  _onSocketReconnectFailed() {
    this.log.debug(`${this.logPrefix}._onSocketReconnectFailed(): failed to reconnect`);
  }

  /**
   * It's socket's `disconnect` event handler
   * Fired upon disconnection
   * @param {String} reason 
   */
  _onSocketDisconnect(reason) {
    const connectionLost = ['ping timeout', 'transport close', 'transport error'].indexOf(reason) !== -1 ? true : false;

    this._dispatch(connectionStatusChanged({
      couchdbName: this.name,
      connected: false,
      connectionLost
    }));

    this.log.info(`${this.logPrefix}._onSocketDisconnect(): socket disconnected:`, reason);
  }

  /**
   * It's socket's `BE_MASTER` message handler
   * @param {String} clientId 
   */
  _onSocketMasterElected(clientId, callback) {
    //stop manages pending/failed writes
    this._cancelManagePendingAndFailedWrites();

    this.master = true;

    //Acknowledment to server
    callback({ success: true });

    //broadcast to other clients
    sysend.broadcast(`master-tab-elected`, { couchdbName: this.name, clientId });

    //Dispatch redux action
    this._dispatch(masterChanged({
      couchdbName: this.name,
      master: true
    }));

    //send pending writeDocs to remote
    this._sendPendingWritesToRemote();

    //start listening localStorage
    this._listenNewWriteRequests();

    //start scheduler to remove write-docs requests from localStorage which are already responded
    this._scheduleWriteRequestRemoval();
    this.log.info(`${this.logPrefix}._onSocketMasterChanged(): Master elected: clientId=`, clientId);
  }

  /**
   * It starts scheduler task, which runs every 10 seconds and will delete all the writeDocs.$requestId keys whose 
   * responseWrittenAt is before 10 seconds.
   */
  _scheduleWriteRequestRemoval() {
    this.log.debug(`${this.logPrefix}._scheduleWriteRequestRemoval(): scheduler for writeDocs request remove is started`);

    this._schedulerToRemoveWriteRequests = setInterval(() => {
      let keys = Object.keys(window.localStorage);
      keys.forEach(key => {
        if (!key.startsWith(`dreamdb.${this.name}.writeDocs`)) {
          return;
        }

        let requestId = key.split(".")[3];
        let value = JSON.parse(window.localStorage.getItem(`dreamdb.${this.name}.writeDocs.${requestId}`));
        if (!value.response) {
          return;
        }

        let responseWrittenAt = Math.floor(value.responseWrittenAt / 1000);
        let now = Math.floor(new Date().getTime() / 1000);
        let diff = now - responseWrittenAt;

        if (diff > 10) {
          window.localStorage.removeItem(`dreamdb.${this.name}.writeDocs.${requestId}`);
          this.log.debug(`${this.logPrefix}._scheduleWriteRequestRemoval(): responded writeDocs's request removed from localStorage, requestId=${requestId}`);
        }
      })
    }, 10000);
  }

  /**
   * It cancels scheduler task
   */
  _cancelWriteRequestScheduler() {
    if (!this._schedulerToRemoveWriteRequests) {
      return;
    }

    clearInterval(this._schedulerToRemoveWriteRequests);
    this._schedulerToRemoveWriteRequests = undefined;
    this.log.debug(`${this.logPrefix}._cancelWriteRequestScheduler(): scheduler is cancelled.`)
  }

  /**
   * It sends pending write docs to remote server from localStorage.
   */
  _sendPendingWritesToRemote() {
    let pendingWriteRequests = Object.keys(window.localStorage).filter((key) => {
      return key.startsWith(`dreamdb.${this.name}.writeDocs`);
    });

    pendingWriteRequests.forEach(key => {
      let requestId = key.split(".")[3];
      let value = JSON.parse(window.localStorage.getItem(key));

      if (value.response) {
        return;
      }

      this._pendingWriteDocsRequests[requestId] = { requestId, value };
    });

    this._sendDocsToRemoteInRequestedOrder();
  }

  /**
   * It's socket's `DOC_CHANGES` message handler
   * @param {Object} request { db = dbName, docs = Document[] }
   * @param {Function} callback 
   */
  async _onSocketDocChanges(request, callback) {
    try {
      const { db, docs } = request;
      this.log.info(`${this.logPrefix}._onSocketDocChanges(): Updated docs on remote.`, docs);
      let databaseRef = this.databases[db];

      //Creates new database instance if not available in cache
      //In case of multiple clients, slave client has performed queries from different databases than master client
      if(!databaseRef) {
        databaseRef = this.db(db);
      }

      let response = await databaseRef.localDb.saveRemoteDocsToLocal(docs);
      callback({ success: true });
      localDbSyncManagers.get(`${this.name}-${db}`).sync();
      this.log.info(`${this.logPrefix}._onSocketDocChanges(): Remote docs saved in local database.`, response);
    } catch (err) {
      this.log.error(`${this.logPrefix}._onSocketDocChanges(): Error while saving remote docs in local database: `, err);
    }
  }

  /**
   * It's socket's PING message handler.
   * It acknowledges the message.
   * @param {Object} request Currently it's empty object.
   * @param {Function} callback 
   */
  async _onPing(request, callback) {
    try {
      this.log.debug(`${this.logPrefix}._onPing(): ping received.`);
      callback({ success: true });
    } catch (err) {
      this.log.error(`${this.logPrefix}._onPing(): failed: `, err);
    }
  }

  /**
   * It's socket's `DATABASE_DELETED` message handler.
   * It removes pouchdb database of given dbName
   * @param {Object} request { dbName }
   * @param {Function} callback 
   */
  async _onDatabaseDeleted(request, callback) {
    const { dbName } = request;
    try {
      let databaseRef = this.databases[dbName];

      if(!databaseRef) {
        databaseRef = this.db(dbName);
      }

      await databaseRef.destroy();
      delete this.databases[dbName];
    } catch (err) {
      this.log.error(`${this.logPrefix}._onDatabaseDeleted(): Error while delete local database. dbName=${dbName}, error=`, err);
    } finally {
      callback({ success: true });
      this.emit('couchdb-database-deleted', { dbName });
      this._dispatch(databaseDeleted({
        couchdbName: this.name,
        dbName,
      }));
    }
  }

  /**
   * It starts listening localStorage changes.
   */
  _listenNewWriteRequests() {
    this.log.info(`${this.logPrefix}._listenNewWriteRequests(): starting listening localStorage to manage new write requests.`);
    window.addEventListener('storage', this.handleNewWritesRequest);
  }

  /**
   * It stops listening localStorage changes.
   */
  _unlistenNewWriteRequests() {
    this.log.info(`${this.logPrefix}._unlistenNewWriteRequests(): stopped listening localStorage to manage new write requests..`);
    window.removeEventListener('storage', this.handleNewWritesRequest);
  }

  /**
   * It starts listening localStorage to manage pending and failed writes.
   */
  _listenLocalStorageChanges() {
    window.addEventListener('storage', this._managePendingAndFailedWrites);
    this.log.info(`${this.logPrefix}._listenLocalStorageChanges(): started listening localStorage to manage pending and failed writes.`);
  }

  /**
   * It stops listening localStorage to manage pending and failed writes.
   */
  _cancelManagePendingAndFailedWrites() {
    window.removeEventListener('storage', this._managePendingAndFailedWrites);
    this.log.info(`${this.logPrefix}._cancelManagePendingAndFailedWrites(): stopped listening localStorage to manage pending and failed writes.`);
  }

  /**
   * It called on localStorage changed.
   * It reads requests from localStorage and accordingly dispatches `WRITE_DOCS_METADATA` action to manage pendingWrites and failedWrites.
   * @param {StorageEvent} e 
   */
  _managePendingAndFailedWrites(e) {
    let key = e.key || (e.detail && e.detail.key);
    let newValue = e.newValue || (e.detail && e.detail.newValue);

    //Returns if newValue is undefined
    //NOTE:: It mostly happened when key/value removed from localStorage.
    if (!newValue) {
      return;
    }

    if (!key.startsWith(`dreamdb.${this.name}.writeDocs`)) {
      return;
    }

    let requestId = key.split(".")[3];
    let value = JSON.parse(newValue);

    let { dbName, docs, requestedAt, response } = value;
    let state = this.store.getState();

    let pendingWritesDocs = pendingWritesByDbName(state, this.name, dbName) || {};
    let failedWritesDocs = failedWritesByDbName(state, this.name, dbName) || {};

    let pendingDocs = { ...pendingWritesDocs };
    let failedDocs = { ...failedWritesDocs };

    //update pendingDocs and failedDocs
    this._updatePendingAndFailedWrites(docs, pendingDocs, failedDocs, response, requestedAt);

    this._dispatch(writeDocsMetadata({
      couchdbName: this.name,
      dbName,
      pendingDocs,
      failedDocs
    }));

    //persists in localStorage
    this._persistPendingAndFailedWrites(dbName, pendingDocs, failedDocs);

    this.log.info(`${this.logPrefix}._managePendingAndFailedWrites(): managed pendingWrites and failedWrites for requestId=${requestId}.`);
  }

  /**
   * It updates given pendingDocs and failedDocs according to server response.
   * @param {Array} docs requested docs
   * @param {Array} pendingDocs current pendingDocs
   * @param {Array} failedDocs current failedDocs
   * @param {Object} response response from server
   * @param {TimeStamp} requestedAt requested time
   */
  _updatePendingAndFailedWrites(docs, pendingDocs, failedDocs, response, requestedAt) {
    if (response && response.error) {

      //Remove from pending writes
      docs.forEach(doc => {
        failedDocs[doc._id] = { error: response.error.code, reason: response.error.message };
        delete pendingDocs[doc._id];
      });
      return;
    }

    //Handle server response
    if (response && response.response) {
      let remoteFailedDocs = response.response;

      //Remove from pending writes
      docs.forEach(doc => {
        delete pendingDocs[doc._id];
      });

      //Add failed docs in failedWrites
      remoteFailedDocs.forEach(doc => {
        failedDocs[doc.id] = { error: doc.error, reason: doc.reason };
      });
      return;
    }

    //Handle requested docs
    docs.forEach(doc => {
      pendingDocs[doc._id] = requestedAt;
      delete failedDocs[doc._id];
    });
  }

  /**
   * It persists pendingWrites and failedWrites data in localStorage.
   * @param {String} dbName 
   * @param {Object} pendingDocs 
   * @param {Object} failedDocs 
   */
  _persistPendingAndFailedWrites(dbName, pendingDocs, failedDocs) {
    try {
      let data = JSON.parse(window.localStorage.getItem(`dreamdb.pendingFailedWrites`)) || {};

      data = set(data, `couchdb-${this.name}.pendingWrites.${dbName}`, pendingDocs);
      data = set(data, `couchdb-${this.name}.failedWrites.${dbName}`, failedDocs);

      window.localStorage.setItem(`dreamdb.pendingFailedWrites`, JSON.stringify(data));
    } catch (error) {
      this.log.error(`${this.logPrefix}._persistPendingAndFailedWrites(): Error while get data from localStorage: `, error);
    }
  }

  /**
   * It sends writes requests to the server in requested order. 
   * It sends new request after previous request is completed.
   */
  async _sendDocsToRemoteInRequestedOrder() {
    let aSortedPendingRequests = sortBy(Object.values(this._pendingWriteDocsRequests), 'value.requestedAt');

    if (!aSortedPendingRequests.length) {
      return;
    }

    for (let i = 0; i < aSortedPendingRequests.length; i++) {
      let { requestId, value } = aSortedPendingRequests[i];

      await this._saveDocsOnRemote(requestId, value);
      delete this._pendingWriteDocsRequests[requestId];
      this.log.debug(`${this.logPrefix}._sendDocsToRemoteInRequestedOrder(): document is saved to remote. pendingRequests=`, this._pendingWriteDocsRequests);
    }

    this._sendDocsToRemoteInRequestedOrder();
  }

  /**
   * It send docs to remote and save response on localStorage given by server.
   * @param {String} requestId 
   * @param {Object} value { dbName, docs } 
   */
  async _saveDocsOnRemote(requestId, value) {
    return new Promise(async (resolve, reject) => {
      let { dbName, docs } = value;

      let callback = async (response) => {

        if(this._authStatus === AuthStatus.IN_PROGRESS && response.error && response.error.status === 401) {
          await this._waitTillAuthStatusBecomesFinal();

          this.socket.emit('WRITE_DOCS', {
            db: dbName,
            docs
          }, callback);
          return;
        }

        if(response.error) {
          this.log.error(`${this.logPrefix}._saveDocsOnRemote(): remote save failed. dbName=${dbName}, docs=`, docs, `error=`, response.error);
        }

        //save remote response in localStroge
        let value = JSON.parse(window.localStorage.getItem(`dreamdb.${this.name}.writeDocs.${requestId}`));

        //It mostly happened when master client is changed and pending docs sent by newly elected master client. So, request would removed from localStorage.
        if (!value) {
          resolve();
          return;
        }

        value = { ...value, response, responseWrittenAt: new Date().getTime() };
        window.localStorage.setItem(`dreamdb.${this.name}.writeDocs.${requestId}`, JSON.stringify(value));

        // need to trigger custom event as master tab wont get `storage` event on remote db save.
        // so dispatched manully to resolve promise of master tab's write request
        const event = new CustomEvent('storage', {
          detail: {
            key: `dreamdb.${this.name}.writeDocs.${requestId}`,
            newValue: JSON.stringify(value)
          }
        });

        window.dispatchEvent(event);
        resolve();
        this.log.debug(`${this.logPrefix}._saveDocsOnRemote(): document is saved on remoteDB successfully. requestId=${requestId}`);
      };

      if(this._authStatus === AuthStatus.IN_PROGRESS) {
        await this._waitTillAuthStatusBecomesFinal();
      }

      let documents = compact(docs);

      if(!documents || !documents.length) {
        this.log.error(`${this.logPrefix}._saveDocsOnRemote(): failed to save on remote. docs have invalid value. dbName=${dbName}, docs=`, docs);

        window.localStorage.removeItem(`dreamdb.${this.name}.writeDocs.${requestId}`);
        //we resolved promise to proceed other pending requests in queue
        resolve();
        return;
      }

      docs = documents;

      this.socket.emit('WRITE_DOCS', {
        db: dbName,
        docs
      }, callback);
    });
  }

  /**
   * It returns a promise which is resolved when couchdb's authStatus is changed to the final status
   * (either DONE or INVALID_CREDENTIAL). It’s never rejected.
   * @returns {Promise}
   */
  _waitTillAuthStatusBecomesFinal() {
    return new Promise((resolve) => {
      let authStatus = this.authStatus;
      if (authStatus === AuthStatus.DONE || authStatus === AuthStatus.INVALID_CREDENTIAL) {
        resolve();
        return;
      }

      const listener = (status) => {
        if (status === AuthStatus.DONE || status === AuthStatus.INVALID_CREDENTIAL) {
          resolve();
          this.off('auth-status-changed', listener);
        }
      }

      this.on(`auth-status-changed`, listener);
    });
  }

  /**
   * It's handler of `master-tab-elected` broadcast message
   * @param {Object} object contains couchdbName and clientId 
   */
  _onMasterChanged({ couchdbName, clientId }) {
    if (couchdbName !== this.name) {
      return;
    }

    //Skip if current client is not master
    if (!this.master) {
      this.log.debug(`${this.logPrefix}._onMasterChanged(): new master client elected. clientId=${clientId}`);
      return;
    }

    this.master = false;

    //starts manages pending/failed writes
    this._listenLocalStorageChanges();

    //stops listening localStorage for the new request
    this._unlistenNewWriteRequests();

    //cancel scheduler task which is removing responded write-docs requests
    this._cancelWriteRequestScheduler();

    //Dispactch redux action
    this._dispatch(masterChanged({
      couchdbName: this.name,
      master: false
    }));

    this.log.debug(`${this.logPrefix}._onMasterChanged(): new master client elected. clientId=${clientId}`);
  }

  /**
   * It's handler of `dreamdb-dispose` broadcast message
   * @param {Object} {couchdbName} contains couchdb name 
   */
  _onDispose({ couchdbName }) {
    if (couchdbName !== this.name) {
      return;
    }

    //Dispatch redux action `destroy`
    this._dispatch(destroy({
      couchdbName: this.name
    }));

    //Disconnect all databases
    this.disconnect();
    utils._resetPromises(this._name);

    this.emit('destroyed', { name: this.name });
    this.log.debug(`${this.logPrefix}._onDispose(): disposed. couchdbName=${this.name}`);
  }
}
